import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Navigate, Route, Routes, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {useAuth} from "../../providers/AuthProvider";
import {useMutation} from "@tanstack/react-query";
import Client from "../../models/Client";
import SupplierKeyClientsStep from "./components/SupplierKeyClientsStep";
import SupplierAwardsStep from "./components/SupplierAwardsStep";
import SupplierInfoStep from "./components/SupplierInfoStep";
import PageNotFound from "../../components/errors/PageNotFound";
import SupplierLinksDocumentsStep from "./components/SupplierLinksDocumentsStep";
import SupplierTalentResourcesStep from "./components/SupplierTalentResourcesStep";
import ReactDOM from "react-dom";
import {useMount} from "react-use";
import { Helmet } from "react-helmet";

const supplierUpdateRequest = async (clientId, params) => {
    TB.auth().SupplierUpdateRequest = TB.request(
        `/api/clients/${clientId}/update`,
        params
    ).headers({
        'Content-Type': 'multipart/form-data'
    });
    console.log('supplierUpdateRequest', TB.auth().SupplierUpdateRequest)

    let response = await TB.auth().SupplierUpdateRequest.post().catch(e => {
        console.warn('ERROR: supplierUpdate', e);
    })

    if (!response || !TB.has(response, 'success.data.client') || !TB.has(response, 'success.data.auth')) {
        TB.auth().SupplierUpdateRequest.didHandleError = await TB.auth().SupplierUpdateRequest.handleError();
        if (TB.auth().SupplierUpdateRequest.didHandleError
            && TB.auth().SupplierUpdateRequest.shouldRerun) {
            return supplierUpdateRequest(clientId, params);
        }

        return false;
    }

    return response
}

const DEFAULT_REQUEST_PARAMS = [
    'name',
    'size',
    'website',
    'location',
    'approved_email_domains',
    'logo_path',
    'banner_image_path',
    'about',
    'offers_service_work',
    'offers_byo_work',
]
const SupplierManageProfile = (props) => {
    const [queryParams, setQueryParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    const formRef = useRef(null);

    const {auth, update: updateAuth, appropriate, logout} = useAuth();

    const [isDisabled, setIsDisabled] = useState(false);

    const [stepSubmitDisabled, setStepSubmitDisabled] = useState(false);

    const [supplierOrg, setSupplierOrg] = useState(() => {
        return auth.supplierOrg();
    });


    const updateSupplierManageProfileMutation = useMutation(
        ['supplierProfileUpdate', supplierOrg.id],
        params => supplierUpdateRequest(supplierOrg.id, params)
    )

    const profileCompleted = useMemo(() => {
        return auth.supplierOrg?.()?.hasCompletedSupplierProfile()
    }, [isDisabled, updateSupplierManageProfileMutation?.isLoading]);

    const activeSteps = useMemo(() => {
        return [
            {
                path: 'about',
                label: 'Agency Info',
                isCompleted: profileCompleted,
                needsCompletion: !profileCompleted,
                icon: 'fas fa-info-circle',
                requestParams: []
            },
            {
                path: 'links-documents',
                label: 'Links & Documents',
                isCompleted: true,
                isDisabled: !profileCompleted,
                icon: 'fas fa-link',
                requestParams: ['profile_links', 'profile_documents',]
            },
            {
                path: 'work-samples',
                label: 'Work Samples',
                isCompleted: true,
                isDisabled: !profileCompleted,
                icon: 'fas fa-briefcase',
                requestParams: ['key_clients']
            },
            {
                path: 'awards-certifications',
                label: 'Awards & Certifications',
                isCompleted: true,
                isDisabled: !profileCompleted,
                icon: 'fas fa-trophy',
                requestParams: ['awards', 'certifications']
            },
            {
                path: 'talent-resources',
                label: 'Talent Resources',
                isCompleted: true,
                isDisabled: !profileCompleted,
                needsCompletion: !auth.supplierOrg?.().total_supplied_talent_users,
                icon: 'fas fa-users'
            },
        ]
    }, [profileCompleted, isDisabled, updateSupplierManageProfileMutation?.isLoading, auth.supplierOrg?.().total_supplied_talent_users]);

    const [lastSavedStep, setLastSavedStep] = useState(() => activeSteps[0])

    const currentStepIndex = useMemo(() => {
        return TB.findIndex(activeSteps, (step, index) => {
            return location.pathname.includes(step.path)
        })
    }, [location?.pathname, activeSteps]);

    const currentStep = useMemo(() => {
        return activeSteps[currentStepIndex]
    }, [currentStepIndex, activeSteps]);

    const nextStep = useMemo(() => {
        if (currentStepIndex !== -1) {
            return activeSteps[currentStepIndex + 1]
        }
        return activeSteps[currentStepIndex]
    }, [currentStepIndex, activeSteps]);

    const prevStep = useMemo(() => {
        if (currentStepIndex !== -1) {
            return activeSteps[currentStepIndex - 1]
        }

        return activeSteps[currentStepIndex]
    }, [currentStepIndex, activeSteps]);

    useEffect(() => {
        if (!supplierOrg || currentStepIndex !== -1) {
            return;
        }
        if (!lastSavedStep) {
            // do something else??
        }

        return goToStep(lastSavedStep, true)
    }, [supplierOrg, currentStepIndex])

    useMount(() => {
        if (auth.supplierOrg() && (
            !auth.supplierOrg?.()?.hasCompletedSupplierProfile() || !auth.supplierOrg?.()?.total_supplied_talent_users
        )) {
            TB.alert('confirm', 'Complete Supplier Profile',
                'Fill out some basic profile information for your agency and invite talent resources to begin managing opportunities.',
                {title: 'Ok'}
            )
        }
    })
    const isReviewing = () => {
        return false
    }

    const saveAndExitInvalidForm = async () => {
        if (TB.AlertIsShowing) {
            await TB.event('Alert@hide');
            await TB.wait(400);
            console.log('saveAndExitInvalidForm', 'WAITING')
        }
        let exitAnyways = await TB.sysAlert('talent.application_save_and_exit_button_clicked');

        console.log('saveAndExitInvalidForm', {exitAnyways})
        if (exitAnyways) {
            return navigate('/supplier/dashboard', {replace: true})
        }
    }

    const goToStep = (step, replace = false) => {
        let route = {
            ...location,
            pathname: step.path
        }

        if (replace) {
            return navigate(route, {replace: true});
        }
        return navigate(route)
    }

    const toggleDisabled = (submitDisabled) => {
        return setStepSubmitDisabled(submitDisabled)
    }

    const onUpdate = async (modelProps) => {
        if (isDisabled || updateSupplierManageProfileMutation.isLoading) return;
        let updates = {...supplierOrg}
        for (let [key, value] of Object.entries(modelProps)) {
            updates = TB.set(updates, key, value)
        }
        return setSupplierOrg(updates);
    };

    const onSaveStep = async (modelProps, skipGoToNext = false) => {
        if (isDisabled || updateSupplierManageProfileMutation.isLoading) return;
        let updates = {...supplierOrg}
        for (let [key, value] of Object.entries(modelProps)) {
            updates = TB.set(updates, key, value)
        }
        return onSubmit(updates, false, skipGoToNext)
    }

    const onSubmit = async (params, exitApplication = false, skipGoToNext = false) => {
        if (isDisabled || updateSupplierManageProfileMutation.isLoading) return;

        if (params) {

            if (formRef?.current) {
                console.log('onSubmit', formRef?.current)
                let invalid = formRef.current.forceValidate();

                if (invalid) {
                    const node = ReactDOM.findDOMNode(invalid);

                    return $('html, body').animate({scrollTop: $(node).offset().top - 50}, 800);
                }
            }
            await setIsDisabled(true)

            let updatedOrg;
            try {
                if (currentStep?.requestParams) {
                    let requestParams = TB.pick(params, [...DEFAULT_REQUEST_PARAMS, ...currentStep?.requestParams]);

                    let response = await updateSupplierManageProfileMutation.mutateAsync({...requestParams});

                    if (response) {
                        if (response.success.data.auth) {
                            await updateAuth({...response.success.data.auth});
                        }

                        updatedOrg = Client.make({...(response.success.data.client)});
                    }
                }else {
                    updatedOrg = supplierOrg;
                }

            } catch (e) {
                updatedOrg = false;
                console.warn('ERROR: onSubmit', e);

                if (TB.auth().SupplierUpdateRequest.didFail &&
                    (TB.auth().SupplierUpdateRequest.errorType === TB.Request.ErrorTooLong ||
                        TB.auth().SupplierUpdateRequest.errorType === TB.Request.ErrorLocal)) {

                    setIsDisabled(false)

                    return await TB.alert('error',
                        TB.trans('api.system_maintenance.title'),
                        TB.trans('api.system_maintenance.message', {code: 'XXX'}), [
                            {
                                title: 'Okay',
                            }
                        ]
                    )
                }
            }

            if (!updatedOrg) {
                /*
                * Update form with server errors
                * */
                console.log('onSubmit', TB.auth().SupplierUpdateRequest)
                if (await TB.auth().talentHasInputResponseError()) {
                    // await routeRef.current?.handleInputResponseError(TB.auth().SupplierUpdateRequest);
                }


                if (exitApplication) return saveAndExitInvalidForm();

                return setIsDisabled(false)
            }


            await setSupplierOrg(updatedOrg);
        }


        if (exitApplication) {
            await setIsDisabled(false);

            return navigate('/supplier/dashboard', {replace: true})
        }

        if (skipGoToNext) {
            await setIsDisabled(false);
            TB.toast('Profile Updated');
            return;
        }

        if (nextStep) {
            if (currentStepIndex > activeSteps.indexOf(lastSavedStep.path)) {
                await setLastSavedStep(() => currentStep)
            }
            await setIsDisabled(false)

            return goToStep(nextStep);
        }

        await setIsDisabled(false)

        let redirect;

        if (!TB.auth().isAppropriate(location)) {
            redirect = appropriate();
        }

        if (redirect) {
            navigate(redirect)
        }

        if (currentStep === TB.last(activeSteps)) {
            await TB.alert('success', 'Profile Updated Successfully',
                'Your profile is now completed. You can edit this information at any time', [
                    {title: 'Go to dashboard', click: () => navigate('/supplier/dashboard')},
                    {
                        title: 'Continue editing', click: () => window.scrollTo({
                            top: 0,
                            left: 0,
                            behavior: 'smooth'
                        })
                    }
                ]
            );
        }
    }

    if (!currentStep) {
        return null
    }

    if (!profileCompleted && currentStep?.path !== "about") {
        return <Navigate to={'about'} replace/>;
    }

    // Fix for showing progress text when percent is small, i.e 10% round up to 15

    let isLastStep = currentStepIndex === activeSteps.length - 1,
        lastStepRemaining;// = supplierOrg.lastStepRemaining();

    let routeProps = {
        isDisabled: isDisabled || stepSubmitDisabled,
        supplierOrg,
        onChange: onUpdate,
        onSave: onSaveStep,
    }
    return (
        <>
            <Helmet>
                <title>{activeSteps[currentStepIndex]?.label} | Supplier | Wripple</title>
            </Helmet>

            <div className="talent-application-container supplier-application-container">
                <div className="talent-application-form-container">
                    <div className="talent-app-nav p-0 w-full lg:w-3/12 xl:w-2/12" style={{borderTop: '1px solid #E5E5E5'}}>
                        <h3 className="font-bold">
                            Agency Profile
                        </h3>
                        {activeSteps.map(step => (
                            <button
                                key={step.path}
                                type="button"
                                className={TB.mapClass('step-title', {
                                    'isCompleted': !step.isDisabled && step.isCompleted,
                                    "inActive": step.isDisabled,
                                    'isActive': location.pathname.includes(step.path),
                                })}
                                disabled={step.isDisabled || updateSupplierManageProfileMutation.isLoading}
                                onClick={() => navigate(step.path)}
                            >
                                <i className={TB.mapClass("text-[#90A0B7]", {
                                    "text-danger": step.needsCompletion,
                                    [step.icon]: true
                                })}/>

                                {step.label}
                            </button>
                        ))}
                    </div>
                    <div className="w-full lg:w-9/12 xl:w-10/1">
                        <div className="talent-application px-4 md:px-8 xl:w-10/12 w-full mr-auto ml-0">

                            <Routes>
                                <Route index element={<Navigate to={'about'} replace/>}/>
                                <Route path="about"
                                    element={<SupplierInfoStep ref={formRef} {...routeProps}/>}/>
                                <Route path="work-samples"
                                    element={<SupplierKeyClientsStep {...routeProps}/>}/>
                                <Route path="links-documents"
                                    element={<SupplierLinksDocumentsStep {...routeProps}/>}/>
                                <Route path="awards-certifications"
                                    element={<SupplierAwardsStep {...routeProps}/>}/>
                                <Route path="talent-resources/*"
                                    element={<SupplierTalentResourcesStep {...routeProps}/>}/>

                                <Route path={'*'} element={<PageNotFound/>}/>

                            </Routes>

                        </div>
                    </div>
                </div>


                <div className="talent-app-footer">
                    {!location.pathname.includes('invite') && !location.pathname.includes('edit') && (
                        <React.Fragment>
                            <div className="lg:w-9/12 xl:w-10/12 ml-auto flex">
                                {(currentStepIndex > 0) && (
                                    <button
                                        type="button"
                                        onClick={() => goToStep(prevStep, true)}
                                        disabled={isDisabled || stepSubmitDisabled || currentStepIndex === 0}
                                        className="btn btn-outline-dark">
                                        {TB.trans('talent.talent_application.buttons.back')}
                                    </button>
                                )}


                                {profileCompleted && auth.supplierOrg?.().total_supplied_talent_users > 0 && (
                                    <div className="save-and-exit">
                                        <button type="button"
                                                disabled={isDisabled || stepSubmitDisabled}
                                                onClick={() => onSubmit(supplierOrg, true)}
                                                className="btn btn-outline-dark">
                                            {TB.trans('talent.talent_app_step.buttons.save_exit')}
                                        </button>
                                    </div>
                                )}
                                <button
                                    type="button"
                                    disabled={isDisabled || stepSubmitDisabled || (isLastStep && !auth.supplierOrg?.().total_supplied_talent_users)}
                                    onClick={() => onSubmit(supplierOrg)}
                                    className="btn btn-primary ml-4">
                                    {isLastStep ? TB.trans('talent.talent_application.buttons.submit') :
                                        TB.trans('talent.talent_application.buttons.continue')
                                    }
                                </button>
                            </div>
                            {profileCompleted && auth.supplierOrg?.().total_supplied_talent_users > 0 && (
                                <div className="md:w-full save-and-exit-mobile">
                                    <button type="button"
                                            disabled={isDisabled || stepSubmitDisabled}
                                            onClick={() => onSubmit(supplierOrg, true)}
                                            className="btn btn-link">
                                        {TB.trans('talent.talent_app_step.buttons.save_exit')}
                                    </button>
                                </div>
                            )}
                        </React.Fragment>
                    )}

                </div>
            </div>
        </>
    );
}

export default SupplierManageProfile